import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import toast from 'react-hot-toast';

interface ChangePriorityModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPriority: string;
  onChangePriority: (priority: string) => Promise<void>;
}

const PRIORITY_OPTIONS = [
  {
    value: 'LOW',
    label: 'Low',
    description: 'Can be addressed in regular workflow',
    color: 'text-success-600 dark:text-success-400',
    bgColor: 'bg-success-100 dark:bg-success-900/20',
    borderColor: 'border-success-500',
  },
  {
    value: 'MEDIUM',
    label: 'Medium',
    description: 'Should be addressed soon',
    color: 'text-primary-600 dark:text-primary-400',
    bgColor: 'bg-primary-100 dark:bg-primary-900/20',
    borderColor: 'border-primary-500',
  },
  {
    value: 'HIGH',
    label: 'High',
    description: 'Requires prompt attention',
    color: 'text-warning-600 dark:text-warning-400',
    bgColor: 'bg-warning-100 dark:bg-warning-900/20',
    borderColor: 'border-warning-500',
  },
  {
    value: 'URGENT',
    label: 'Urgent',
    description: 'Needs immediate attention',
    color: 'text-danger-600 dark:text-danger-400',
    bgColor: 'bg-danger-100 dark:bg-danger-900/20',
    borderColor: 'border-danger-500',
  },
];

export default function ChangePriorityModal({
  isOpen,
  onClose,
  currentPriority,
  onChangePriority,
}: ChangePriorityModalProps) {
  const [selectedPriority, setSelectedPriority] = useState(currentPriority);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedPriority(currentPriority);
    }
  }, [isOpen, currentPriority]);

  const handleSubmit = async () => {
    if (selectedPriority === currentPriority) {
      toast.error('Please select a different priority');
      return;
    }

    try {
      setIsSubmitting(true);
      await onChangePriority(selectedPriority);
      const newPriorityLabel = PRIORITY_OPTIONS.find(p => p.value === selectedPriority)?.label;
      toast.success(`Priority changed to ${newPriorityLabel}`);
      onClose();
    } catch (error) {
      console.error('Failed to change priority:', error);
      toast.error('Failed to change priority');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pt-16 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-md pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                    <ClockIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                    Change Priority
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-3">
                {PRIORITY_OPTIONS.map((priority) => {
                  const isSelected = selectedPriority === priority.value;
                  const isCurrent = currentPriority === priority.value;

                  return (
                    <button
                      key={priority.value}
                      onClick={() => setSelectedPriority(priority.value)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? `${priority.borderColor} ${priority.bgColor}`
                          : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-300 dark:hover:border-secondary-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span
                              className={`font-semibold ${
                                isSelected ? priority.color : 'text-secondary-900 dark:text-white'
                              }`}
                            >
                              {priority.label}
                            </span>
                            {isCurrent && (
                              <span className="text-xs px-2 py-0.5 bg-secondary-200 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 rounded">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
                            {priority.description}
                          </p>
                        </div>
                        {isSelected && (
                          <div className={`w-5 h-5 ${priority.bgColor} rounded-full flex items-center justify-center flex-shrink-0 ml-3`}>
                            <div className={`w-3 h-3 ${priority.color.replace('text-', 'bg-')} rounded-full`} />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-200 dark:border-secondary-700">
                <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={selectedPriority === currentPriority || isSubmitting}
                  className="min-w-24"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      <span>Saving...</span>
                    </div>
                  ) : (
                    'Change Priority'
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
